import { Button, CardFooter } from "@heroui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useAudit } from "../Audit/hook";

type DeleteButtonProps = {
  onPress: () => void;
  isDisabled?: boolean;
  children: React.ReactNode;
};

export const DeleteButton = ({ onPress, isDisabled, children }: DeleteButtonProps) => (
  <Button
    color="danger"
    variant="flat"
    radius="full"
    size="sm"
    onPress={onPress}
    isDisabled={isDisabled}
  >
    {children}
  </Button>
);

type FormActionsNewProps = {
  onReset: () => void;
  onDelete: () => void;
  isDirty?: boolean;
};

export const FormActionsNew = ({
  onReset,
  onDelete,
  isDirty,
}: FormActionsNewProps) => {
  const { t } = useTranslation();

  return (
    <CardFooter className="flex justify-between px-0 py-1">
      <div className="flex gap-3">
        <Button
          color="primary"
          radius="full"
          size="sm"
          type="submit"
          isDisabled={isDirty === false}
        >
          {t("actions.create")}
        </Button>

        <Button
          color="primary"
          variant="flat"
          radius="full"
          size="sm"
          onPress={onReset}
        >
          {t("actions.reset")}
        </Button>
      </div>

      <Button
        color="danger"
        variant="light"
        radius="full"
        size="sm"
        startContent={<IoMdClose />}
        onPress={onDelete}
      >
        {t("actions.cancel")}
      </Button>
    </CardFooter>
  );
};

type FormActionsUpdateProps = {
  onReset: () => void;
  onDelete: () => void;
  isDirty?: boolean;
  elementId: number | string;
  client: string;
  allowDelete?: boolean;
};

export const FormActionsUpdate = ({
  onReset,
  onDelete,
  isDirty,
  elementId,
  client,
  allowDelete = false,
}: FormActionsUpdateProps) => {
  const { t } = useTranslation();
  const { isDeleted } = useAudit(elementId, client);

  return (
    <CardFooter className="flex justify-start gap-3 px-0 py-1">
      <Button
        color={isDirty === false || isDeleted ? "default" : "primary"}
        radius="full"
        size="sm"
        type="submit"
        isDisabled={isDirty === false || isDeleted}
      >
        {t("actions.save")}
      </Button>

      <Button
        color="primary"
        variant="flat"
        radius="full"
        size="sm"
        onPress={onReset}
        isDisabled={isDirty === false || isDeleted}
      >
        {t("actions.reset")}
      </Button>

      {allowDelete ? (
        <DeleteButton onPress={onDelete} isDisabled={isDeleted}>
          {t("actions.delete")}
        </DeleteButton>
      ) : null}
    </CardFooter>
  );
};
